import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Leaf, Lock, User } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user) nav("/", { replace: true });
  }, [user, nav]);
  if (user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await login(username, password);
      toast.success("Welcome");
      nav("/", { replace: true });
    } catch (ex) {
      const msg = formatError(ex);
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div
        className="hidden md:flex flex-col justify-between p-10 bg-primary text-primary-foreground relative overflow-hidden"
        style={{
          backgroundImage: "linear-gradient(180deg, hsla(164,47%,11%,0.85), hsla(164,47%,7%,0.95)), url('https://images.pexels.com/photos/6694180/pexels-photo-6694180.jpeg')",
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="flex items-center gap-3">
          <Leaf className="w-8 h-8 text-secondary" strokeWidth={2.5} />
          <div>
            <div className="font-display font-bold text-2xl tracking-tight">ESSENCE</div>
            <div className="text-xs uppercase tracking-widest text-white/60">Inventory Control</div>
          </div>
        </div>
        <div className="max-w-md">
          <div className="data-label text-secondary mb-4">Multi-location · Container-level</div>
          <h1 className="text-4xl font-display font-bold leading-tight mb-4">
            Essential oils, chemicals & butters — traced to the drum.
          </h1>
          <p className="text-white/70">
            Every kilo logged. Every container coded. Every move accountable.
          </p>
        </div>
        <div className="text-xs text-white/40 font-mono">v1.0 · phone-friendly · offline-safe queue soon</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="md:hidden flex items-center gap-2 mb-4">
            <Leaf className="w-7 h-7 text-primary" strokeWidth={2.5} />
            <div className="font-display font-bold text-2xl">ESSENCE</div>
          </div>
          <div>
            <h2 className="font-display text-3xl font-bold">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Warehouse credentials issued by admin.
            </p>
          </div>

          <div className="space-y-3">
            <label className="block">
              <span className="data-label">Username</span>
              <div className="relative mt-1">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="login-username"
                  className="field pl-9"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                />
              </div>
            </label>
            <label className="block">
              <span className="data-label">Password</span>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="login-password"
                  type="password"
                  className="field pl-9"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
            </label>
          </div>

          {err && (
            <div data-testid="login-error" className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-sm px-3 py-2">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="btn-primary w-full"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>

        </form>
      </div>
    </div>
  );
}
